import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Fuel, Gauge, Calendar, ExternalLink, CarFront, ShoppingBag } from "lucide-react";
import { api } from "@/lib/api";
import { Chapter } from "@/components/landing/Chapter";

const LBC_BOUTIQUE = "https://www.leboncoin.fr/boutique/7148300/auto_meca_pro.htm";

const VehicleCard = ({ v, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: (index % 3) * 0.1 }}
    data-testid={`vehicle-card-${v.id}`}
    className="group rounded-md border border-white/10 bg-[#0E1322] overflow-hidden transition-[border-color,box-shadow,transform] duration-300 hover:border-[#0066FF]/60 hover:shadow-[0_0_40px_rgba(0,102,255,0.15)] hover:-translate-y-1"
  >
    <div className="relative h-52 overflow-hidden bg-[#07090E]">
      {v.image ? (
        <img
          src={v.image}
          alt={v.titre}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0E1322] to-[#0066FF]/10">
          <CarFront className="w-14 h-14 text-[#0066FF]/40" />
        </div>
      )}
      <div className="absolute top-4 right-4 rounded-full bg-[#0066FF] text-white font-display font-bold px-4 py-1.5 text-sm shadow-[0_0_20px_rgba(0,102,255,0.5)]">
        {Number(v.prix).toLocaleString("fr-FR")} €
      </div>
    </div>
    <div className="p-6">
      <h3 className="font-display font-bold text-lg tracking-tight">{v.titre}</h3>
      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-400 font-mono-tech">
        <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[#00E5FF]" />{v.annee}</span>
        <span className="flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5 text-[#00E5FF]" />{Number(v.km).toLocaleString("fr-FR")} km</span>
        <span className="flex items-center gap-1.5"><Fuel className="w-3.5 h-3.5 text-[#00E5FF]" />{v.energie}</span>
      </div>
      <a
        data-testid={`vehicle-link-${v.id}`}
        href={v.lien || LBC_BOUTIQUE}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 w-full rounded-full border border-white/15 hover:border-[#EC5A13] hover:bg-[#EC5A13]/10 text-white text-sm font-semibold py-2.5 flex items-center justify-center gap-2 transition-[border-color,background-color] duration-300"
      >
        Voir l'annonce
        <ExternalLink className="w-3.5 h-3.5" />
      </a>
    </div>
  </motion.div>
);

export const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    api.get("/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
  }, []);

  return (
    <section id="vehicules" data-testid="vehicles-section" className="relative py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Chapter num="03" title="Véhicules à vendre" accent="Occasions sélectionnées & garanties" />

        {vehicles.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {vehicles.map((v, i) => (
              <VehicleCard key={v.id} v={v} index={i} />
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            className="rounded-md border border-dashed border-white/15 bg-[#0E1322]/50 p-12 text-center"
            data-testid="vehicles-empty-state"
          >
            <CarFront className="w-10 h-10 text-[#0066FF] mx-auto mb-4" />
            <p className="text-slate-300 text-base">Nos occasions arrivent et partent vite.</p>
            <p className="text-slate-500 text-sm mt-2">Retrouvez le stock complet et à jour sur notre boutique leboncoin.</p>
          </motion.div>
        )}

        <motion.a
          data-testid="vehicles-leboncoin-cta"
          href={LBC_BOUTIQUE}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-10 group relative overflow-hidden rounded-md border border-[#EC5A13]/40 bg-gradient-to-r from-[#EC5A13]/15 via-[#0E1322] to-[#0E1322] p-7 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 transition-[border-color,box-shadow] duration-300 hover:border-[#EC5A13] hover:shadow-[0_0_40px_rgba(236,90,19,0.2)]"
        >
          <div className="flex items-center gap-5">
            <span className="w-12 h-12 rounded-sm bg-[#EC5A13] flex items-center justify-center shrink-0">
              <ShoppingBag className="w-6 h-6 text-white" />
            </span>
            <div>
              <div className="font-display font-bold text-xl">Notre boutique leboncoin</div>
              <div className="text-sm text-slate-400 mt-1">Toutes nos annonces de véhicules d'occasion, mises à jour en temps réel.</div>
            </div>
          </div>
          <span className="rounded-full bg-[#EC5A13] text-white font-semibold px-6 py-3 flex items-center gap-2 transition-transform duration-300 group-hover:-translate-y-0.5 whitespace-nowrap">
            Voir toutes les annonces
            <ExternalLink className="w-4 h-4" />
          </span>
        </motion.a>
      </div>
    </section>
  );
};
